import { useEffect, useMemo, useState } from 'react';
import { canUseWriteFeatures, isTenantAdminOrSystemAdmin } from './access';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export function useCompanyWriteAccess(companyId) {
  const hasWriteFeatureAccess = canUseWriteFeatures();
  const isAdmin = isTenantAdminOrSystemAdmin();
  const [companyAccessMap, setCompanyAccessMap] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadCompanyAccess = async () => {
      if (!hasWriteFeatureAccess || isAdmin) {
        setCompanyAccessMap({});
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/company-access/me`, { headers: buildHeaders() });
        if (!response.ok) {
          if (!cancelled) {
            setCompanyAccessMap({});
          }
          return;
        }
        const data = await response.json();
        if (cancelled) {
          return;
        }
        const next = Array.isArray(data)
          ? data.reduce((acc, item) => {
              if (item?.companyId != null && item?.accessLevel) {
                acc[String(item.companyId)] = item.accessLevel;
              }
              return acc;
            }, {})
          : {};
        setCompanyAccessMap(next);
      } catch {
        if (!cancelled) {
          setCompanyAccessMap({});
        }
      }
    };

    loadCompanyAccess();

    return () => {
      cancelled = true;
    };
  }, [hasWriteFeatureAccess, isAdmin]);

  const accessLevel = companyId ? companyAccessMap[String(companyId)] ?? null : null;
  const canWrite = useMemo(() => {
    if (!hasWriteFeatureAccess) {
      return false;
    }
    if (isAdmin) {
      return true;
    }
    return accessLevel === 'READ_WRITE';
  }, [accessLevel, hasWriteFeatureAccess, isAdmin]);

  return {
    accessLevel,
    canWrite,
    isAdmin
  };
}
